import { Injectable, inject } from '@angular/core';
import { HttpService } from 'eas-core-lib';
import { BehaviorSubject, debounceTime, distinctUntilChanged, map, Observable, of, Subject, switchMap, zip } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApplicationRolesResponse, MultiOrgFilter, Organization, OrganizationsRolesResponse, RoleGroupsResponse, 
  RoleRequest, RoleRequestResponse, RoleResponse, System, WorkflowHistoryResponse
} from '../models/role-request.models';

@Injectable({
  providedIn: 'root'
})
export class RoleRequestService {
  private http = inject(HttpService);

  private apiUrl = environment.armApiEndpoint;
  private organizationRoles: any = null;

  private multiOrgFiltersSubject = new BehaviorSubject<any>({});
  private orgChangedSubject = new Subject<boolean>();

  // Utility to fetch organization roles, caching the result
  getOrganizationsRoles(): Observable<OrganizationsRolesResponse> {
    if (this.organizationRoles) {
      return of({ ...this.organizationRoles });
    }
    return this.http.get(`${this.apiUrl}/api/v1/UserProfile/OrganizationsRoles`);
  }
  
  // Fetch organizations roles by specific assign group
  getOrganizationsRolesByAssignGroup(assignGroupKey: string, term: string, parentOrg: number): Observable<Organization[]> {
    if(parentOrg) {
      return this.http.get(
        `${this.apiUrl}/api/v1/RoleRequest/Orgs/ParentOrgKey/${parentOrg}`
      );
    } else {
      return this.http.get(
        `${this.apiUrl}/api/v1/RoleRequest/OrganizationsByAssignGroup?roleAssignGroupKey=${assignGroupKey}&searchTerm=${term}`
      );
    }
  }

  // Fetch user roles by organization key
  getUserRolesByOrgKey(orgKey: number): Observable<OrganizationsRolesResponse> {
    return this.http.get(`${this.apiUrl}/api/v1/UserProfile/OrganizationsRoles/${orgKey}`);
  }

  // Fetch all application roles
  getAllApplicationRoles(): Observable<ApplicationRolesResponse> {
    return this.http.get(`${this.apiUrl}/api/v1/RoleSearch/applicationRoles`);
  }

  // Fetch application roles by role key
  getApplicationRolesByRoleKey(roleKey: string): Observable<ApplicationRolesResponse> {
    return this.http.get(`${this.apiUrl}/api/v1/RoleSearch/applicationRoles?roleKey=${roleKey}`);
  }

  // Fetch application roles by system key
  getApplicationRolesBySystem(systemKey: string): Observable<ApplicationRolesResponse> {
    return this.http.get(`${this.apiUrl}/api/v1/RoleSearch/applicationRoles?systemKey=${systemKey}`);
  }

  getRolesBySystemAndAssignGroup(systemKey: string, assignGroupKey: string): Observable<RoleResponse> {
    return this.http.get(`${this.apiUrl}/api/v1/RoleRequest/system/${systemKey}/assignGroup/${assignGroupKey}`);
  }

  getRolesByUsageTypeAndAssignGroup(assignGroupKey: string): Observable<RoleResponse> {
    return this.http.get(`${this.apiUrl}/api/v1/RoleRequest/assignGroup/${assignGroupKey}/usageType/0`);
  }

  getAllSystemApplications(): Observable<System[]> {
    return this.http.get(`${this.apiUrl}/api/v1/RoleRequest/Systems`);
  }

  // Submit role request
  roleRequestPost(payload: any, isAdmin = false): Observable<RoleRequest[]> {
    return this.http.post(`${this.apiUrl}/api/v1/RoleRequest?requestAsAdmin=${isAdmin}`, payload) as Observable<RoleRequest[]>;
  }

  // Fetch role requests by person key
  getRoleRequestsByPersonKey(personKey: string): Observable<RoleRequestResponse> {
    return this.http.get(`${this.apiUrl}/api/v1/RoleRequest?personKey=${personKey}`);
  }

  // Fetch role request groups
  getRoleGroups(): Observable<RoleGroupsResponse> {
    return of([
      { roleAssignGroupKey: "1", roleAssignGroupName: "Roles assigned to DEW staff members" },
      { roleAssignGroupKey: "2", roleAssignGroupName: "Roles assigned to State of Ohio Agency staff members" },
      { roleAssignGroupKey: "3", roleAssignGroupName: "Roles assigned to district, school and other educational organization staff members" },
      { roleAssignGroupKey: "4", roleAssignGroupName: "Roles not related to any specific organization" },
    ]);
  }

  // Get role requests (approvals)
  getRoleRequests(): Observable<any[]> {
    return this.http.get(`${this.apiUrl}/api/v1/RoleRequestApprovals`);
  }

  // Approve role requests
  approveRoleRequests(payload: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/api/v1/RoleRequestApprovals`, payload);
  }

  // Fetch employees based on various parameters
  getEmployees(orgId: number, name: string, roleKeys: string[], lastName?: string, dob?: string, ssn4?: string): Observable<any> {
    let url = `${this.apiUrl}/api/v1/RoleSearch/employees?orgKey=${orgId}&name=${name}%`;
    if (lastName) url += `&lastName=${lastName}%`;
    if (dob) url += `&dob=${dob}`;
    if (ssn4) url += `&ssn4=${ssn4}`;
    if (roleKeys.length) url += `&roleKeys=${roleKeys.join(',')}`;
    return this.http.get(url);
  }

  getEmployeesBySupervisor(name: string) : Observable<any> {
    return this.http.get(`${this.apiUrl}/api/v1/RoleRequestApprovals/EmployeesBySupervisor?employeeName=${name}`);
  }

  // Fetch persons by role key
  getPersonsByRoleKey(roleKey: string, orgKey: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/api/v1/RoleSearch/roleKey/${roleKey}/orgKey/${orgKey}`);
  }

  // Fetch roles by person key
  getRolesByPersonKey(personKey: string, fetchResponsibilities?: boolean): Observable<any> {
    const url = fetchResponsibilities ?
      `${this.apiUrl}/api/v1/RoleSearch/personKey/${personKey}?fetchResponsibilities=true` :
      `${this.apiUrl}/api/v1/RoleSearch/personKey/${personKey}`;
    return this.http.get(url).pipe(
      map((res: any) => {
        return res;
      })
    );
  }

  // Store filters data for multiple organizations
  set multiOrgFilters(formData: MultiOrgFilter) {
    this.multiOrgFiltersSubject.next({ ...this.multiOrgFilters, ...formData });
    this.orgChangedSubject.next(true);
  }

  get multiOrgFilters$(): Observable<MultiOrgFilter> {
    return this.multiOrgFiltersSubject.asObservable();
  }

  get multiOrgFilters(): MultiOrgFilter {
    return this.multiOrgFiltersSubject.getValue();
  }

  get isOrgChanged(): Observable<boolean> {
    return this.orgChangedSubject.asObservable();
  }

  // Parse JWT to get the person key
  private parseJwt(token: string): any {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(window.atob(base64).split('').map(c =>
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join(''));
    return JSON.parse(jsonPayload);
  }

  // Get the logged-in person's key
  getLoggedInPersonKey(): string | null {
    const token = JSON.parse(window.sessionStorage.getItem('accessToken'))?.accessToken;
    return token ? this.parseJwt(token).person_key : null;
  }

  getRoleRequestHistory(roleRequest: number): Observable<WorkflowHistoryResponse> {
    return this.http.get(`${this.apiUrl}/api/v1/RoleSearch/RoleRequestHistory/` + roleRequest);
  }

  getPersonAdvancedSearch(lastName: string, birthdate: string, ssn: string): Observable<any> {
    return this.http.get(`${this.apiUrl}/api/v1/RoleSearch/PersonAdvancedSearch/${lastName}/${birthdate}/${ssn}`);
  }

  hasAuthorizationForComponent(assetTemplateKey: number) {
    return this.http.get(`${this.apiUrl}/api/v1/RoleSearch/AssetAuthorizedResponse?assetTemplateKey=${assetTemplateKey}`).pipe(map((res: any) => res?.permission?.read));
  }

  searchEmployee(selectedEmployee: string, isDewStaff: boolean, isDewOrg: boolean) {
     if (selectedEmployee?.length > 2) {
          const orgKey = this.multiOrgFilters.selectedOrganization.orgKey;
          if (!isDewStaff && isDewOrg) {
            return this.getEmployeesBySupervisor(selectedEmployee).pipe(debounceTime(300), distinctUntilChanged(), switchMap(items => {
              let persons = items.personOrgRoleList?.flatMap(x => x.person) || [];
              persons = persons.filter((item, index, self) => index === self.findIndex((obj) => obj.key === item.key));
              persons.forEach(person => {
                const offEmail = person?.emailList?.find(email => email?.emailType?.key == 1)?.emailAddress;
                if (offEmail) {
                  person.emailAddress = offEmail;
                } else {
                  const otherEmail = person?.emailList?.[0]?.emailAddress;
                  person.emailAddress = otherEmail || '';
                }
    
              })
              persons.sort((a, b) => a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName));
              return of(persons)
            }))
          } else {
            let roleKeys = [];
            if (this.multiOrgFilters.selectedRoleGroup.roleAssignGroupKey === '1') {
              roleKeys = ['1100295' /*Employee*/, '1100296' /*Contractor*/];
            }
            let firstName = selectedEmployee;
            let lastName = '';
            if (selectedEmployee.includes(' ')) {
              firstName = selectedEmployee.split(' ')[0];
              lastName = selectedEmployee.split(' ')[1];
            }
            return zip(
              this.getEmployees(orgKey, firstName, roleKeys, lastName),
              this.getEmployees(orgKey, lastName, roleKeys, firstName)
            ).pipe(
              debounceTime(300),
              distinctUntilChanged(),
              switchMap(([firstResponse, secondResponse]) => {
                let persons = [
                  ...firstResponse.personOrgRoleList?.flatMap(x => x.person) || [],
                  ...secondResponse.personOrgRoleList?.flatMap(x => x.person) || []
                ];
                persons = persons.filter((item, index, self) => index === self.findIndex((obj) => obj.key === item.key));
                persons.forEach(person => {
                  const offEmail = person?.emailList?.find(email => email?.emailType?.key == 1)?.emailAddress;
                  if (offEmail) {
                    person.emailAddress = offEmail;
                  } else {
                    const otherEmail = person?.emailList?.[0]?.emailAddress;
                    person.emailAddress = otherEmail || '';
                  }
    
                })
                persons.sort((a, b) => a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName));
                return of(persons);
              })
            );
          }
        } else {
          return of([])
        }
  }

  isRoleAndOrgSelected() {
    return this.multiOrgFilters.selectedOrganization 
                  && (this.multiOrgFilters.selectedRoleGroup 
                  || this.multiOrgFilters.selectedRoleGroup?.roleAssignGroupKey === '4' /* No Org*/);
  }
}
